import React, { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { ContextMenuBuilder, ContextMenu } from '@/components/custom/erp/input-table-v2';
import { RepeatRowsPayload, ErpInputTableV2 } from '@/components/custom/erp';
import { FormFieldActions, FormFieldState } from '../../../hooks';
import { SelectedCellInfo, useDetailPaste } from '../hooks';
import { copyCellValue } from '@/lib/table-handlers';
import { getDataTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface ThueTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  state: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
  onRowClick?: DataTableRowEventHandler<MuaHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<MuaHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onRepeatForRows: (payload: RepeatRowsPayload<MuaHangRecord>) => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onCellCommit?: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<MuaHangRecord[]>; resultField?: string }>
  ) => void;
  onHandleDetail?: () => void;
  isPasting?: boolean;
}

const TAX_ACTION_ORDER = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'detail', 'export', 'pin'] as const;
const noop = () => {};

export const ThueTab: React.FC<ThueTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  state,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onRepeatForRows,
  onCellValueChange,
  onCellCommit,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  onHandleDetail,
  isPasting
}) => {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: MuaHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const [dien_giai] = useWatch({ name: ['dien_giai'] });
  const columns = useMemo(
    () => getDataTableColumns(state, dien_giai, onCellValueChange, onCellCommit),
    [state, dien_giai, onCellValueChange, onCellCommit]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );
  const handleRowId = useCallback((row: MuaHangRecord) => row?.uuid || '', []);
  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: DataTableRenderCellParams) => {
      onCellClick?.(params);

      setContextMenu({
        position: { x: event.clientX, y: event.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [onCellClick]
  );
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      detail: onHandleDetail,
      export: noop,
      pin: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onHandleDetail, onMoveRow, onPasteRow]
  );

  return (
    <>
      <ErpInputTableV2
        rows={rows}
        onRowClick={onRowClick as MuaHangRecord}
        onCellClick={onCellClick as MuaHangRecord}
        onCellContextMenu={handleCellContextMenu as MuaHangRecord}
        autoHeight
        showIndexColumn
        bottomSelector='[data-arito-bottom-bar]'
        selectedRowId={selectedRowUuid || undefined}
        columns={columns}
        getRowId={handleRowId}
        pasteTarget={selectedCell}
        actionLoading={isPasting}
        actionsDisabled={formMode === 'view'}
        actionOrder={TAX_ACTION_ORDER as MuaHangRecord}
        actions={tableActions}
      />
      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() => {
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          });
        }}
      />
    </>
  );
};
